import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, RefreshCw } from 'lucide-react';
import { User, Payment } from '../../types';
import { api } from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/formatters';
import InvoiceRequestModal from '../InvoiceRequestModal';

interface PaymentHistoryTableProps {
    user: User;
}

export const PaymentHistoryTable: React.FC<PaymentHistoryTableProps> = ({ user }) => {
    const [payments, setPayments] = useState<Payment[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const { t } = useTranslation('billing');

    const loadPayments = async () => {
        setLoading(true);
        const data = await api.getPayments(user.id);
        setPayments(data);
        setLoading(false);
    };

    useEffect(() => { loadPayments(); }, [user.id]);

    const handleRequestClick = (paymentId: string) => {
        setSelectedPaymentId(paymentId);
        setIsModalOpen(true);
    };

    const handleConfirmRequest = async () => {
        if (!selectedPaymentId) return;
        setIsProcessing(true);
        try {
            await api.requestInvoice(selectedPaymentId);
            await loadPayments();
            setIsModalOpen(false);
            setSelectedPaymentId(null);
        } catch (e: any) {
            alert(t('history.request_failed') + e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mt-8">
                <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Clock size={20} /> {t('history.title')}</h3>
                    <button onClick={loadPayments}><RefreshCw size={16} className="text-slate-400 hover:text-slate-600" /></button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="px-6 py-4">{t('history.date')}</th>
                                <th className="px-6 py-4">{t('history.amount')}</th>
                                <th className="px-6 py-4">{t('history.status')}</th>
                                <th className="px-6 py-4">{t('history.invoice')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? <tr><td colSpan={4} className="p-6 text-center">{t('history.loading')}</td></tr> :
                                payments.length === 0 ? <tr><td colSpan={4} className="p-6 text-center text-slate-400">{t('history.no_payments')}</td></tr> :
                                    payments.map(p => (
                                        <tr key={p.id}>
                                            <td className="px-6 py-4 font-medium">{formatDate(p.date, user.dateFormat)}</td>
                                            <td className="px-6 py-4 font-bold">{formatCurrency(p.amount, p.currency)}</td>
                                            <td className="px-6 py-4 capitalized">{p.status}</td>
                                            <td className="px-6 py-4">
                                                {p.invoiceRequestStatus === 'sent' ? (
                                                    <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-1 rounded">{t('history.requested')}</span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleRequestClick(p.id)}
                                                        className="text-slate-500 hover:text-blue-600 hover:underline text-xs font-bold transition-colors"
                                                    >
                                                        {t('history.request_invoice')}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <InvoiceRequestModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleConfirmRequest}
                isLoading={isProcessing}
            />
        </>
    );
};
